import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { IMAGE, IMAGE_OR_VIDEO, VIDEO } from 'constants/appConstants';

import MelodyCarousel from 'components/common/MelodyCarousel';
import ReviewGalleryMediaGridElement from 'components/reviews/ReviewGalleryMediaGridElement';
import { SmallLoader } from 'components/Loader';
import { logError } from 'middleware/logger';
import css from 'styles/components/reviews/reviewGalleryMediaGrid.scss';

function shouldRenderMediaItem({ viewMediaType }, mediaItem) {
  const { mediaType } = mediaItem;
  if ([IMAGE, VIDEO].indexOf(mediaType) === -1) {
    logError(`only IMAGE and VIDEO media are currently supported (found mediaItem with type ${mediaType})`);
    return false;
  }
  switch (viewMediaType) {
    case IMAGE_OR_VIDEO: return true;
    case IMAGE: return mediaType === IMAGE;
    case VIDEO: return mediaType === VIDEO;
    default: return false;
  }
}

const makeReviewGalleryMediaGridElement = ({ compactThumbnails, onOpenMediaReview, showVideoThumbnails }, { id: reviewId }, mediaItem, mediaIndex) => (
  <ReviewGalleryMediaGridElement
    key={mediaItem.msaMediaUrl || mediaItem.mediaUrl}
    compact={compactThumbnails}
    mediaIndex={mediaIndex}
    mediaItem={mediaItem}
    onOpenMediaReview={onOpenMediaReview}
    showVideoThumbnails={showVideoThumbnails}
    reviewId={reviewId} />
);

function makeRenderableMedia(props, review, acc, mediaItem, mediaIndex) {
  // filter by image and video media only and map them to markup
  if (shouldRenderMediaItem(props, mediaItem)) {
    acc.push(makeReviewGalleryMediaGridElement(props, review, mediaItem, mediaIndex));
  }
  return acc;
}

function makeMediaForReview(props, acc, review) {
  const { reviewGalleryMedia } = review;
  const reducer = makeRenderableMedia.bind(null, props, review);
  // maps all media in this review's gallery into markup
  return acc.concat(reviewGalleryMedia.reduce(reducer, []));
}

function makeMedia(props) {
  const { reviews } = props;
  const reducer = makeMediaForReview.bind(null, props);
  // maps all media in all reviews into markup
  return reviews.reduce(reducer, []);
}

const makeMediaCarousel = media => (
  <MelodyCarousel padScrollArea={false} showDots={false} snap={MelodyCarousel.snap.none}>
    {media}
  </MelodyCarousel>
);

const ReviewGalleryMediaGrid = props => {
  const { isLoading, limit, limitHeight, useCarousel, placementStyle } = props;

  const allMedia = makeMedia(props);
  const shownMedia = limit ? allMedia.slice(0, limit) : allMedia;
  const mediaMarkup = useCarousel ? makeMediaCarousel(shownMedia) : shownMedia;
  return (
    <div className={cn(css.overlayImagesContainer, { [css.limitHeight]: limitHeight }, { [css.loadingMedia]: isLoading })}>
      {isLoading && <SmallLoader/>}
      {!isLoading && <div className={cn(css.reviewsImagesAndVideos, { [placementStyle]: !!placementStyle })}>
        {mediaMarkup}
      </div>}
    </div>
  );
};

ReviewGalleryMediaGrid.defaultProps = {
  compactThumbnails: false,
  limitHeight: false,
  useCarousel: false,
  viewMediaType: IMAGE_OR_VIDEO
};

ReviewGalleryMediaGrid.propTypes = {
  compactThumbnails: PropTypes.bool.isRequired,
  productId: PropTypes.string,
  reviews: PropTypes.array.isRequired,
  viewMediaType: PropTypes.oneOf([IMAGE, VIDEO, IMAGE_OR_VIDEO]),
  isLoading: PropTypes.bool,
  limit: PropTypes.number,
  limitHeight: PropTypes.bool.isRequired,
  useCarousel: PropTypes.bool,
  onOpenMediaReview: PropTypes.func.isRequired
};

ReviewGalleryMediaGrid.contextTypes = {
  testId: PropTypes.func
};

export default ReviewGalleryMediaGrid;
